import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AnimatePresence, motion } from 'framer-motion';

import { requestYesNo, type YesNoResult } from '@/api/client';
import OrnateFrame from '@/components/OrnateFrame';
import TarotCard from '@/components/TarotCard';
import { useAudio } from '@/hooks/useAudio';
import { useTelegram } from '@/hooks/useTelegram';

/**
 * Yes / No card — free, instant, snappy. The hook to bring the user back
 * for a "quick question" without thinking about cost or time. Result is
 * shareable to a friend via Telegram's native share picker.
 */

const ANSWER_COLOR: Record<YesNoResult['answer'], string> = {
  yes:   'text-emerald-300 border-emerald-400/50',
  no:    'text-rose-300 border-rose-400/50',
  maybe: 'text-gold-200 border-gold-400/50',
};

const ANSWER_GLOW: Record<YesNoResult['answer'], string> = {
  yes:   'drop-shadow(0 0 20px rgba(110, 231, 183, 0.5))',
  no:    'drop-shadow(0 0 20px rgba(251, 113, 133, 0.5))',
  maybe: 'drop-shadow(0 0 20px rgba(232, 195, 86, 0.5))',
};

export default function YesNo() {
  const { t } = useTranslation();
  const audio = useAudio();
  const { haptic, tg } = useTelegram();

  const [question, setQuestion] = useState('');
  const [result, setResult] = useState<YesNoResult | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // After "Ask a different question" we surface an inline reminder above
  // the form so the user notices they should reformulate, not retype.
  const [showResetNote, setShowResetNote] = useState(false);

  const ask = async () => {
    if (busy) return;
    setBusy(true);
    setError(null);
    setShowResetNote(false);
    try {
      const r = await requestYesNo(question.trim() || undefined);
      setResult(r);
      audio.play('shuffle');
      if (r.answer === 'yes') haptic?.notificationOccurred('success');
      else if (r.answer === 'no') haptic?.notificationOccurred('warning');
      else haptic?.impactOccurred('light');
    } catch (e) {
      setError((e as Error).message);
      haptic?.notificationOccurred('error');
    } finally {
      setBusy(false);
    }
  };

  const reset = () => {
    setResult(null);
    setQuestion('');
    setError(null);
    setShowResetNote(true);
  };

  const shareResult = () => {
    if (!result) return;
    // The share text carries the message + a CTA hook with our app link,
    // so the recipient can come in and ask their own question.
    const q = result.question ? `«${result.question}»\n` : '';
    const text = `${q}${result.message}\n\n${t('yes_no.share_cta')}`;
    if (tg?.openTelegramLink) {
      const url = 'https://t.me/share/url'
        + `?url=${encodeURIComponent(result.share_url)}`
        + `&text=${encodeURIComponent(text)}`;
      tg.openTelegramLink(url);
    } else {
      navigator.clipboard?.writeText(`${text}\n${result.share_url}`).catch(() => {});
    }
  };

  return (
    <main className="mx-auto max-w-md px-5 pb-24 pt-2">
      <h1 className="mb-1 text-center font-serif text-2xl text-gold-300">
        🃏 {t('yes_no.title')}
      </h1>
      <p className="mb-6 text-center text-xs italic text-gold-300/60">
        {t('yes_no.subtitle')}
      </p>

      <AnimatePresence mode="wait">
        {!result ? (
          <motion.section
            key="form"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
          >
            <AnimatePresence>
              {showResetNote && (
                <motion.div
                  key="reset-note"
                  initial={{ opacity: 0, y: -6, height: 0 }}
                  animate={{ opacity: 1, y: 0, height: 'auto' }}
                  exit={{ opacity: 0, y: -6, height: 0 }}
                  transition={{ duration: 0.3 }}
                  className="mb-3 overflow-hidden rounded-lg border border-gold-500/30
                             bg-gold-500/10 px-3 py-2 text-[12px] leading-snug text-gold-200"
                >
                  ✦ {t('yes_no.ask_again_note')}
                </motion.div>
              )}
            </AnimatePresence>
            <OrnateFrame className="card-glow mb-4">
              <label className="block">
                <span className="mb-2 block text-[10px] uppercase tracking-widest text-gold-500/60">
                  {t('yes_no.input_label')}
                </span>
                <input
                  type="text"
                  value={question}
                  onChange={(e) => setQuestion(e.target.value)}
                  placeholder={t('yes_no.placeholder')}
                  maxLength={200}
                  className="w-full rounded-lg border border-gold-500/30
                             bg-night-900/60 px-3 py-2 font-serif text-base
                             text-gold-100 outline-none transition
                             placeholder:text-gold-300/35 focus:border-gold-400"
                />
                <p className="mt-1 text-[10px] italic text-gold-300/40">
                  {t('yes_no.optional_hint')}
                </p>
              </label>
            </OrnateFrame>

            <p className="mb-4 px-1 text-[11px] italic leading-snug text-gold-300/55">
              ⚠ {t('yes_no.repeat_warning')}
            </p>

            <button
              type="button"
              onClick={ask}
              disabled={busy}
              className="btn-primary w-full disabled:opacity-40"
            >
              {busy ? t('yes_no.drawing') : t('yes_no.draw_button')}
            </button>

            {error && (
              <p className="mt-3 text-center text-xs text-rose-400">{error}</p>
            )}
          </motion.section>
        ) : (
          <motion.section
            key="result"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center"
          >
            {/* Animated card flip */}
            <div className="mb-5 flex justify-center">
              <motion.div
                initial={{ rotateY: 180, opacity: 0 }}
                animate={{ rotateY: 0, opacity: 1 }}
                transition={{ duration: 0.8, ease: 'easeOut' }}
              >
                <TarotCard card={result.card} size={160} />
              </motion.div>
            </div>

            {/* Big answer chip */}
            <motion.div
              initial={{ scale: 0.6, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.5, type: 'spring', stiffness: 200, damping: 12 }}
              className={`mx-auto mb-4 inline-flex items-center justify-center
                          rounded-full border-2 bg-night-900/60 px-8 py-3
                          font-serif text-3xl uppercase tracking-[0.2em]
                          ${ANSWER_COLOR[result.answer]}`}
              style={{ filter: ANSWER_GLOW[result.answer] }}
            >
              {t(`yes_no.answer_${result.answer}`)}
            </motion.div>

            {result.question && (
              <p className="mb-3 font-serif text-sm italic text-gold-300/70">
                «{result.question}»
              </p>
            )}

            <motion.p
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8, duration: 0.5 }}
              className="mb-6 px-2 font-serif italic leading-relaxed text-gold-200"
            >
              {result.message}
            </motion.p>

            <div className="space-y-2">
              <button
                type="button"
                onClick={shareResult}
                className="btn-ghost w-full"
              >
                ✦ {t('yes_no.share')}
              </button>
              <button
                type="button"
                onClick={reset}
                className="btn-primary w-full"
              >
                {t('yes_no.ask_again')}
              </button>
            </div>
          </motion.section>
        )}
      </AnimatePresence>
    </main>
  );
}
